import React from "react";
import { SiteLink } from "../shared";
import { ScoreBadge, ScoreParts, tierOf } from "./ScoreBadge";
import { FitNotes } from "./FitNotes";
import { Phone, Mail, UserSearch, Sparkles, Trash2, ChevronDown, Linkedin, Send, Pencil } from "lucide-react";

export const STAGES = ["New", "Contacted", "Replied", "Interested", "NDA", "LOI", "Passed"];
const when = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

export const TargetRow = ({ t, checked, expanded, busy, onCheck, onExpand, onStage, onEnrich, onScore, onEmail, onEdit, onDelete }) => {
  const place = [t.city, t.state].filter(Boolean).join(", ") || t.address || "";
  return (
    <>
      <tr className={`om-row ${tierOf(t.score)} ${expanded ? "open" : ""}`} data-testid={`om-row-${t.id}`}>
        <td><input type="checkbox" checked={!!checked} onChange={onCheck} data-testid={`om-check-${t.id}`} /></td>
        <td><ScoreBadge score={t.score} rationale={t.rationale} testid={`om-score-${t.id}`} /></td>
        <td>
          <button className="om-co" onClick={onExpand} data-testid={`om-expand-${t.id}`}>
            <b>{t.company}</b><ChevronDown size={12} className={expanded ? "rot" : ""} />
          </button>
          <SiteLink url={t.website} name={t.company} testid={`om-site-${t.id}`} />
          <div className="om-sub">{[t.type, t.services, t.founded ? `est. ${t.founded}` : null].filter(Boolean).join(" · ").slice(0, 90)}</div>
        </td>
        <td style={{ fontSize: 11 }}>
          {t.owner ? <><b>{t.owner}</b>{t.ownerTitle ? <div className="om-sub">{t.ownerTitle}</div> : null}</> : <button className="om-add-owner" onClick={onEdit} data-testid={`om-add-owner-${t.id}`}>+ Add owner</button>}
          {t.linkedin && <a className="site-link" href={t.linkedin} target="_blank" rel="noreferrer" title="LinkedIn" data-testid={`om-li-${t.id}`}><Linkedin size={11} /></a>}
        </td>
        <td>
          {t.phone ? <a className="om-tel" href={`tel:${t.phone.replace(/[^\d+]/g, "")}`} data-testid={`om-tel-${t.id}`}><Phone size={12} />{t.phone}</a> : <span className="om-sub">—</span>}
          {t.email ? <div className="om-mail" data-testid={`om-email-${t.id}`}><Mail size={11} />{t.email}</div> : null}
        </td>
        <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>{place}{t.distanceMiles != null && <div className="om-sub">~{t.distanceMiles} mi</div>}</td>
        <td style={{ fontSize: 11 }}>{t.type || <span className="om-sub">—</span>}</td>
        <td>
          <select className={`inp om-stage st-${t.stage.replace(/\s/g, "").toLowerCase()}`} style={{ minWidth: 104 }} value={t.stage} onChange={(e) => onStage(e.target.value)} data-testid={`om-stage-${t.id}`}>
            {STAGES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </td>
        <td style={{ whiteSpace: "nowrap" }}>
          <button className="btn btn-sm" onClick={onEnrich} disabled={busy === "enrich"} title="Find the owner via Apollo + web" data-testid={`om-enrich-${t.id}`}><UserSearch size={12} />{busy === "enrich" ? "…" : "Find owner"}</button>
          <button className="btn btn-sm" onClick={onScore} disabled={busy === "score"} title="Score bolt-on fit with AI" data-testid={`om-rescore-${t.id}`}><Sparkles size={12} /></button>
          <button className="btn btn-sm" onClick={onEmail} disabled={!t.email} title={t.email ? "Compose a seller email" : "Find the owner's email first"} data-testid={`om-mail-${t.id}`}><Send size={12} /></button>
          <button className="btn btn-sm" onClick={onEdit} title="Edit details / enter owner manually" data-testid={`om-edit-${t.id}`}><Pencil size={12} /></button>
          <button className="btn btn-sm" onClick={onDelete} data-testid={`om-del-${t.id}`}><Trash2 size={12} /></button>
        </td>
      </tr>
      {expanded && (
        <tr className="om-detail" data-testid={`om-detail-${t.id}`}>
          <td colSpan={9}>
            <div className="om-detail-grid">
              <div>
                <div className="section-label">Why this score</div>
                <div className="om-rationale">{t.rationale || "Not scored yet — hit the sparkle button or “Score selected”."}</div>
                <ScoreParts parts={t.scoreParts} distanceMiles={t.distanceMiles} />
                <FitNotes notes={t.fitNotes} label="Why this could be the perfect acquisition" testid={`om-fit-${t.id}`} onUseOpener={(line) => onEmail(line)} canEmail={!!t.email} />
              </div>
              <div>
                <div className="section-label">Details</div>
                <div className="om-kv">
                  {t.address && <div><span>Address</span>{t.address}</div>}
                  {t.website && <div><span>Website</span><a href={/^https?:/.test(t.website) ? t.website : `https://${t.website}`} target="_blank" rel="noreferrer">{t.website.replace(/^https?:\/\/(www\.)?/, "")}</a></div>}
                  {t.reviews && <div><span>Reviews</span>{t.reviews}</div>}
                  {t.notes && <div><span>Notes</span>{t.notes}</div>}
                  <div><span>Source</span>{t.source}{t.createdAt ? ` · added ${when(t.createdAt)}` : ""}</div>
                </div>
                {(t.activities || []).length > 0 && (
                  <>
                    <div className="section-label" style={{ marginTop: 10 }}>Activity</div>
                    <div className="om-acts">{[...t.activities].reverse().slice(0, 6).map((a, i) => <div key={i}><span>{when(a.at)}</span>{a.note}</div>)}</div>
                  </>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
